// components/Sidebar/index.jsx
import { useState, useRef, useEffect } from 'react';
import {
  Drawer, Box, Typography, Divider, IconButton, Badge, List,
  ListItemButton, ListItemIcon, ListItemText, useTheme, useMediaQuery,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';

import SidebarMenu from './SidebarMenu';
import AdminMenu from './AdminMenu';
import UserNicknameWithEmoticon from '../Common/UserNicknameWithEmoticon';
import { useUser } from '../../contexts/UserContext';
import SidebarDailyCheckin from './SidebarDailyCheckin';

const DRAWER_WIDTH = 240;

export default function Sidebar({
  showHamburger = true,
  openExternal,
  setOpenExternal,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [pos, setPos] = useState({ x: window.innerWidth - 60, y: 12 });
  const dragRef = useRef({ active: false, offsetX: 0, offsetY: 0 });

  const [openPC, setOpenPC] = useState(openExternal ?? !isMobile);

  useEffect(() => {
    if (openExternal !== undefined) setOpenPC(openExternal);
  }, [openExternal]);

  useEffect(() => {
    if (openExternal === undefined) setOpenPC(!isMobile);
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (setOpenExternal) setOpenExternal(openPC);
  }, [openPC, setOpenExternal]);

  const { username, userInfo } = useUser();
  const navigate = useNavigate();
  if (!userInfo) return null;

  const toggleDrawerPC = () => setOpenPC((o) => !o);
  const toggleDrawerMobile = () => setOpenPC((o) => !o);
  const closeDrawerMobile = () => setOpenPC(false);
  const handleAlertClick = () => navigate('/main/mypage?tab=timeline');

  const handleItemClick = () => {
    if (isMobile) closeDrawerMobile();
  };

  const onPointerDown = (e) => {
    dragRef.current = {
      active: true,
      offsetX: e.clientX - pos.x,
      offsetY: e.clientY - pos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    setPos({
      x: Math.min(Math.max(0, e.clientX - dragRef.current.offsetX), window.innerWidth - 48),
      y: Math.min(Math.max(0, e.clientY - dragRef.current.offsetY), window.innerHeight - 48),
    });
  };
  const onPointerUp = () => {
    dragRef.current.active = false;
  };

  const floatingMenuButton =
    isMobile &&
    showHamburger && (
      <IconButton
        onClick={toggleDrawerMobile}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          position: 'fixed',
          left: pos.x,
          top: pos.y,
          zIndex: theme.zIndex.drawer + 2,
          bgcolor: 'white',
          border: '1px solid #ccc',
          boxShadow: 2,
          borderRadius: '50%',
          width: 40,
          height: 40,
          '&:hover': { bgcolor: '#f0f0f0' },
          touchAction: 'none',
        }}
      >
        <MenuIcon />
      </IconButton>
    );

  const cornerMenuButton =
    !isMobile &&
    showHamburger && (
      <IconButton
        onClick={toggleDrawerPC}
        sx={{
          position: 'fixed',
          top: 12,
          left: openPC ? DRAWER_WIDTH + 12 : 12,
          zIndex: theme.zIndex.drawer + 2,
          bgcolor: 'white',
          border: '1px solid #ccc',
          boxShadow: 2,
          borderRadius: '50%',
          width: 40,
          height: 40,
          transition: 'left .25s',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        <MenuIcon />
      </IconButton>
    );

  const drawerContent = (
    <>
      <Box
        sx={{
          pt: 2,
          pb: 1,
          display: 'flex',
          justifyContent: 'center',
          opacity: openPC || isMobile ? 1 : 0,
        }}
      >
        <Box
          component="img"
          src="/logo-sandle.png"
          alt="SanDle Logo"
          sx={{
            width: 120,          // ← 여기서 크기 조절
            maxWidth: '100%',
            objectFit: 'contain',
            transition: 'all .25s ease',
          }}
        />
      </Box>

      <Box sx={{ p: 2, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <UserNicknameWithEmoticon
            username={userInfo?.username || username}
            mainTitle={userInfo?.main_title}
            subTitle={userInfo?.sub_title}
            emoticonUrl={userInfo?.equipped_emoticon}
            emojiText={userInfo?.equipped_emoticon_text}
            size={30}
            twoLine={true}
          />
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ mt: 1, fontSize: '1rem', opacity: openPC || isMobile ? 1 : 0 }}
          >
            {(userInfo.points?.toLocaleString() ?? '0')} Leaf🌿
          </Typography>
        </Box>

        {userInfo?.unread_count !== undefined && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <IconButton onClick={handleAlertClick}>
              <Badge badgeContent={userInfo.unread_count} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Box>
        )}

        {/* ✅ 마이페이지/출석체크 공통 스타일 적용 */}
        <List
          sx={{
            mt: 1,
            '& .MuiListItemButton-root': {
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              // 기본 padding이 컴포넌트마다 달라 보일 때 통일감 줌
              py: 1,
            },
            '& .MuiListItemIcon-root': {
              minWidth: 36, // 아이콘 영역 통일
            },
          }}
        >
          <ListItemButton onClick={() => { handleItemClick(); navigate('/main/mypage'); }}>
            <ListItemIcon><PersonIcon /></ListItemIcon>
            <ListItemText
              primary="마이페이지"
              primaryTypographyProps={{ fontWeight: 500 }}
              sx={{ display: openPC || isMobile ? 'block' : 'none' }}
            />
          </ListItemButton>

          <SidebarDailyCheckin onItemClick={handleItemClick} />
        </List>

        <Divider sx={{ my: 2 }} />

        <SidebarMenu
          onItemClick={handleItemClick}
          dense={!openPC && !isMobile}
          open={openPC || isMobile}
        />
      </Box>

      {(userInfo.role === 'admin' || userInfo.role === 'master') && (
        <Box sx={{ pb: 2 }}>
          <Divider />
          <AdminMenu
            onItemClick={handleItemClick}
            dense={!openPC && !isMobile}
            open={openPC || isMobile}
          />
        </Box>
      )}
    </>
  );

  return (
    <>
      {floatingMenuButton}
      {cornerMenuButton}

      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={openPC}
          onClose={closeDrawerMobile}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="persistent"
          anchor="left"
          open={openPC}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
}
